import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApi } from '../../api/api-context';
import { useBudgetId } from '../../budget/budget-store';
/**
 * Fetches categories for a given budget ID.
 *
 * @param {string} budgetId - The ID of the budget.
 * @param {ReturnType<typeof useApi>} api - API instance for making requests.
 * @returns {Promise<Category[]>} - A promise resolving to the list of categories.
 */
const fetchCategories = async (budgetId, api) => {
    const response = await api.get(`/category/budget/${budgetId}`);
    return response.data;
};
/**
 * Exposed query options for fetching categories.
 *
 * @param {string} budgetId - The ID of the budget.
 * @param {ReturnType<typeof useApi>} api - API instance.
 * @returns Query options for React Query.
 */
export const getCategoriesQueryOptions = (budgetId, api) => queryOptions({
    queryKey: ['categories', budgetId],
    queryFn: () => fetchCategories(budgetId, api),
});
/**
 * React Query hook for fetching categories based on a budget ID.
 *
 * @param {string} budgetId - The ID of the budget.
 * @param {UseCategoriesOptions} options - Optional configuration for the query.
 * @returns React Query response object.
 */
export const useGetCategoriesApi = (budgetId, { queryConfig } = {}) => {
    const api = useApi();
    return useQuery({
        ...getCategoriesQueryOptions(budgetId, api),
        ...queryConfig,
    });
};
/**
 * Hook to get categories for the currently selected budget.
 *
 * @returns {Category[]} - The list of categories for the current budget.
 */
export const useCategories = () => {
    const budgetId = useBudgetId();
    const { data: categories = [] } = useGetCategoriesApi(budgetId);
    return categories;
};
/**
 * Hook to create a map of categories by their ID.
 *
 * @returns {Map<string, Category>} - A map where category IDs are keys and Category objects are values.
 */
export const useCategoriesMap = () => {
    const categories = useCategories();
    return useMemo(() => new Map(categories.map((category) => [category.id, category])), [categories]);
};
